import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit2, Trash2, Eye, EyeOff, X } from 'lucide-react';
import { API_BASE } from '../../lib/api';

const API = API_BASE;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });

const EMPTY_FORM = { title_es: '', title_en: '', slug: '', excerpt_es: '', excerpt_en: '', body_es: '', body_en: '', featured_image: '', category: '', author: 'Mikel Martinez', published: true };

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const fetchPosts = () => {
    axios.get(`${API}/admin/blog`, getAuth())
      .then(res => setPosts(res.data))
      .catch(e => { if (e.response?.status === 401) navigate('/admin'); });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchPosts(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (post) => { setEditing(post); setForm({ title_es: post.title_es, title_en: post.title_en, slug: post.slug, excerpt_es: post.excerpt_es || '', excerpt_en: post.excerpt_en || '', body_es: post.body_es, body_en: post.body_en, featured_image: post.featured_image || '', category: post.category || '', author: post.author, published: post.published }); setShowForm(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/blog/${editing.id}`, form, getAuth());
      } else {
        await axios.post(`${API}/admin/blog`, form, getAuth());
      }
      fetchPosts();
      setShowForm(false);
    } catch (err) {
      alert('Error al guardar');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este post?')) return;
    await axios.delete(`${API}/admin/blog/${id}`, getAuth());
    fetchPosts();
  };

  const autoSlug = (title) => title.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  return (
    <div className="p-8" data-testid="admin-blog-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-poppins font-bold text-2xl text-white">Blog</h1>
          <p className="text-white/40 text-sm font-source-sans mt-1">{posts.length} artículos</p>
        </div>
        <button onClick={openCreate} data-testid="create-post-btn" className="flex items-center gap-2 bg-[#7D468C] text-white px-5 py-2.5 font-poppins font-semibold text-sm hover:bg-[#9D62AD] transition-colors">
          <Plus size={16} /> Nuevo Post
        </button>
      </div>

      {/* Table */}
      <div className="bg-[#1A2639] border border-white/8 overflow-hidden">
        {posts.length === 0 ? (
          <div className="text-center py-16 text-white/30 font-source-sans">No hay posts aún.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-white/8">
              <tr>
                <th className="text-left px-6 py-3 text-white/40 font-poppins font-semibold text-xs uppercase tracking-wide">Título (ES)</th>
                <th className="text-left px-4 py-3 text-white/40 font-poppins font-semibold text-xs uppercase tracking-wide hidden md:table-cell">Categoría</th>
                <th className="text-left px-4 py-3 text-white/40 font-poppins font-semibold text-xs uppercase tracking-wide hidden lg:table-cell">Estado</th>
                <th className="text-right px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {posts.map(post => (
                <tr key={post.id} data-testid={`post-row-${post.id}`} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-poppins font-medium text-white line-clamp-1">{post.title_es}</div>
                    <div className="text-white/35 text-xs font-source-sans mt-0.5">{post.slug}</div>
                  </td>
                  <td className="px-4 py-4 text-white/50 font-source-sans hidden md:table-cell">{post.category || '—'}</td>
                  <td className="px-4 py-4 hidden lg:table-cell">
                    <span className={`text-xs font-poppins font-semibold px-2 py-0.5 ${post.published ? 'bg-green-500/15 text-green-400' : 'bg-white/8 text-white/40'}`}>
                      {post.published ? 'Publicado' : 'Borrador'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEdit(post)} className="p-1.5 text-white/40 hover:text-[#4EBFBF] transition-colors" data-testid={`edit-post-${post.id}`}><Edit2 size={14} /></button>
                      <button onClick={() => handleDelete(post.id)} className="p-1.5 text-white/40 hover:text-red-400 transition-colors" data-testid={`delete-post-${post.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="blog-form-modal">
          <div className="bg-[#1A2639] border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-white/8">
              <h2 className="font-poppins font-bold text-white">{editing ? 'Editar Post' : 'Nuevo Post'}</h2>
              <button onClick={() => setShowForm(false)} className="text-white/40 hover:text-white transition-colors"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/50 text-xs mb-1.5 font-source-sans uppercase tracking-wide">Título ES *</label>
                  <input required value={form.title_es} onChange={e => setForm(f => ({ ...f, title_es: e.target.value, slug: autoSlug(e.target.value) }))} className="w-full bg-[#0F1C2E] border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#7D468C]" />
                </div>
                <div>
                  <label className="block text-white/50 text-xs mb-1.5 font-source-sans uppercase tracking-wide">Título EN *</label>
                  <input required value={form.title_en} onChange={e => setForm(f => ({ ...f, title_en: e.target.value }))} className="w-full bg-[#0F1C2E] border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#7D468C]" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-white/50 text-xs mb-1.5 font-source-sans uppercase tracking-wide">Slug *</label>
                  <input required value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} className="w-full bg-[#0F1C2E] border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#7D468C]" />
                </div>
                <div>
                  <label className="block text-white/50 text-xs mb-1.5 font-source-sans uppercase tracking-wide">Categoría</label>
                  <input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full bg-[#0F1C2E] border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#7D468C]" placeholder="marketing, eventos..." />
                </div>
                <div>
                  <label className="block text-white/50 text-xs mb-1.5 font-source-sans uppercase tracking-wide">Autor</label>
                  <input value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} className="w-full bg-[#0F1C2E] border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#7D468C]" />
                </div>
              </div>
              <div>
                <label className="block text-white/50 text-xs mb-1.5 font-source-sans uppercase tracking-wide">URL Imagen Destacada</label>
                <input value={form.featured_image} onChange={e => setForm(f => ({ ...f, featured_image: e.target.value }))} className="w-full bg-[#0F1C2E] border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#7D468C]" placeholder="https://..." />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/50 text-xs mb-1.5 font-source-sans uppercase tracking-wide">Extracto ES</label>
                  <textarea rows={2} value={form.excerpt_es} onChange={e => setForm(f => ({ ...f, excerpt_es: e.target.value }))} className="w-full bg-[#0F1C2E] border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#7D468C] resize-none" />
                </div>
                <div>
                  <label className="block text-white/50 text-xs mb-1.5 font-source-sans uppercase tracking-wide">Extracto EN</label>
                  <textarea rows={2} value={form.excerpt_en} onChange={e => setForm(f => ({ ...f, excerpt_en: e.target.value }))} className="w-full bg-[#0F1C2E] border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#7D468C] resize-none" />
                </div>
              </div>
              <div>
                <label className="block text-white/50 text-xs mb-1.5 font-source-sans uppercase tracking-wide">Contenido ES *</label>
                <textarea rows={6} required value={form.body_es} onChange={e => setForm(f => ({ ...f, body_es: e.target.value }))} className="w-full bg-[#0F1C2E] border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#7D468C] resize-y" />
              </div>
              <div>
                <label className="block text-white/50 text-xs mb-1.5 font-source-sans uppercase tracking-wide">Contenido EN *</label>
                <textarea rows={6} required value={form.body_en} onChange={e => setForm(f => ({ ...f, body_en: e.target.value }))} className="w-full bg-[#0F1C2E] border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#7D468C] resize-y" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="published" checked={form.published} onChange={e => setForm(f => ({ ...f, published: e.target.checked }))} className="accent-[#7D468C]" />
                <label htmlFor="published" className="text-white/60 text-sm font-source-sans">Publicado</label>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className="bg-[#7D468C] text-white px-6 py-2.5 font-poppins font-semibold text-sm hover:bg-[#9D62AD] transition-colors disabled:opacity-50">
                  {saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear Post')}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="border border-white/20 text-white/60 px-6 py-2.5 font-poppins text-sm hover:border-white/40 transition-colors">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
